import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, subscribePush } from "@/lib/push";
import { useAuth } from "@/contexts/AuthContext";

// Dismissal is remembered PER DEVICE + PER USER for 14 days — the prompt
// must never interrupt active work or reappear on page change. The user.id
// can be "anon" on the first render before auth resolves, so the device key
// is the durable one; both are written on dismiss and either suppresses.
const SNOOZE_DAYS = 14;
const DEVICE_KEY = "buildflow.push.dismissed.device";

// Notification opt-in prompt: shows shortly after login when permission is
// still "default". Yields to the install banner if that is on screen.
export default function PushPrompt() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const snoozeKey = `buildflow.push.dismissed.${user?.id || "anon"}`;
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.id) return; // wait for auth so the key is never the "anon" one
    if (!pushSupported()) return;
    if (Notification.permission !== "default") return;
    const fresh = (k) => {
      const v = Number(localStorage.getItem(k) || 0);
      return v && Date.now() - v < SNOOZE_DAYS * 864e5;
    };
    if (fresh(snoozeKey) || fresh(DEVICE_KEY)) return;
    let retry = null;
    const timer = setTimeout(() => {
      if (!document.querySelector('[data-testid="install-banner"]')) {
        setShow(true);
        return;
      }
      retry = setInterval(() => {
        if (!document.querySelector('[data-testid="install-banner"]')) {
          clearInterval(retry);
          retry = null;
          setShow(true);
        }
      }, 3000);
    }, 8000);
    return () => {
      clearTimeout(timer);
      if (retry) clearInterval(retry);
    };
  }, [snoozeKey, user]);

  const dismiss = () => {
    localStorage.setItem(snoozeKey, String(Date.now()));
    localStorage.setItem(DEVICE_KEY, String(Date.now()));
    setShow(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const ok = await subscribePush();
      if (ok) toast.success(t("push_enabled"));
      else toast.error(t("push_failed"));
    } catch (e) {
      toast.error(t("push_failed"));
    }
    setBusy(false);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="push-banner"
      className="fixed inset-x-4 bottom-[calc(76px+env(safe-area-inset-bottom,0px))] z-50 md:inset-x-auto md:right-6 md:bottom-6 md:w-96 rounded-2xl border border-slate-700 bg-slate-900/95 backdrop-blur p-4 shadow-2xl"
    >
      <button
        onClick={dismiss}
        data-testid="push-dismiss"
        aria-label={t("push_dismiss")}
        className="absolute right-3 top-3 rounded-full p-1 text-slate-400 hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-800 text-brand">
          <Bell className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold text-white">{t("push_title")}</div>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-400">{t("push_body")}</p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          onClick={enable}
          disabled={busy}
          data-testid="push-enable"
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white active:scale-95 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
          {t("push_action")}
        </button>
        <button
          onClick={dismiss}
          data-testid="push-not-now"
          className="rounded-full px-4 py-2.5 text-xs font-semibold text-slate-400 hover:text-white"
        >
          {t("push_dismiss")}
        </button>
      </div>
    </div>
  );
}
